import { useState, type ComponentType } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleHelp,
  Clock3,
  Database,
  HardDrive,
  Mail,
  MessagesSquare,
  Network,
  Phone,
  RefreshCw,
  Server,
  ShieldCheck,
} from 'lucide-react';
import { api } from '../../api';
import { useAuth } from '../../auth';
import { userHasRole } from '../../roles';
import { PageHeading } from '../../components/ui/PageHeading';
import { TelephonyDiagnosticsPanel } from '../../telephony';
import './monitoring.css';

export type MonitoringStatus = 'HEALTHY' | 'DEGRADED' | 'DOWN' | 'UNKNOWN';
export type MonitoringCategory = 'CORE' | 'MESSAGING' | 'OPERATIONS' | 'SECURITY' | 'TELEPHONY';

export type MonitoringComponent = {
  key: string;
  label: string;
  category: MonitoringCategory;
  status: MonitoringStatus;
  summary: string;
  observedAt: string;
  latencyMs: number;
  details: Record<string, string | number | boolean | null | Array<string | number | boolean | null>>;
  actionable: boolean;
};

export type MonitoringIncident = {
  id: string;
  incident_type: 'SYSTEM' | 'TELEPHONY';
  incident_key: string;
  source: string;
  code: string;
  resource: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  status: 'ACTIVE' | 'RESOLVED';
  title: string;
  message: string;
  occurrences: number;
  first_seen_at: string;
  last_seen_at: string;
  resolved_at: string | null;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
};

export type MonitoringSnapshot = {
  status: MonitoringStatus;
  observedAt: string;
  counts: { healthy: number; degraded: number; down: number; unknown: number; activeIncidents: number };
  components: MonitoringComponent[];
  incidents: MonitoringIncident[];
};

const REFRESH_MS = 30_000;

const STATUS: Record<MonitoringStatus, { label: string; title: string; note: string; icon: ComponentType }> = {
  HEALTHY: { label: 'Всё работает', title: 'Все системы в норме', note: 'Критических отклонений в инфраструктуре не обнаружено.', icon: CheckCircle2 },
  DEGRADED: { label: 'Нужно внимание', title: 'Система требует внимания', note: 'Основные функции доступны, но одна или несколько проверок требуют реакции.', icon: AlertTriangle },
  DOWN: { label: 'Есть сбой', title: 'Есть недоступные сервисы', note: 'Часть функций может быть недоступна. Активные инциденты перечислены ниже.', icon: AlertTriangle },
  UNKNOWN: { label: 'Нет данных', title: 'Состояние уточняется', note: 'Мониторинг ещё не получил достаточно данных от сервисов.', icon: CircleHelp },
};

const CATEGORIES: Array<{ id: MonitoringCategory; title: string; note: string }> = [
  { id: 'CORE', title: 'Ядро системы', note: 'База данных, сессии и файловое хранилище' },
  { id: 'OPERATIONS', title: 'Эксплуатация', note: 'Фоновые процессы и защита данных' },
  { id: 'MESSAGING', title: 'Коммуникации', note: 'Каналы обращений и почта' },
  { id: 'SECURITY', title: 'Безопасность', note: 'Защита входа и блокировки' },
  { id: 'TELEPHONY', title: 'Телефония', note: 'FreeSWITCH, SIP и медиаканалы' },
];

const ICONS: Record<string, ComponentType> = {
  postgresql: Database,
  redis: Server,
  minio: HardDrive,
  worker: Activity,
  scheduler: Clock3,
  telegram: MessagesSquare,
  max: MessagesSquare,
  mail: Mail,
  bitrix24: Network,
  security: ShieldCheck,
  backups: HardDrive,
  telephony: Phone,
};

const DETAIL_LABELS: Record<string, string> = {
  ageSeconds: 'Давность сигнала, сек.',
  queueDepth: 'Заданий в очереди',
  failedTotal: 'Ошибок обработки',
  processStatus: 'Состояние процесса',
  cycleDurationMs: 'Последний цикл, мс',
  enabledChannels: 'Включено каналов',
  activeChannels: 'Активно каналов',
  errorChannels: 'Каналов с ошибкой',
  enabled: 'Интеграция включена',
  lastSuccessAt: 'Последний успешный обмен',
  syncAt: 'Последняя синхронизация',
  processed24h: 'Обработано за 24 часа',
  errorCount: 'Ошибок',
  smtpEnabled: 'Исходящая почта',
  imapEnabled: 'Входящая почта',
  available: 'Агент доступен',
  running: 'Агент запущен',
  jailEnabled: 'Правила применены',
  currentBanned: 'Заблокировано сейчас',
  totalBanned: 'Блокировок всего',
  backupStatus: 'Резервная копия',
  maintenanceStatus: 'Обслуживание',
  deepVerifyStatus: 'Проверка восстановления',
  gatewayReachable: 'FreeSWITCH',
  registrationStatus: 'SIP-регистрация',
  turnReady: 'TURN',
  activeCalls: 'Активных звонков',
  webRtcRegistered: 'WebRTC-регистраций',
};

function formatDate(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function detailValue(value: MonitoringComponent['details'][string]) {
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
  if (Array.isArray(value)) return value.join(' · ') || '—';
  if (value === null || value === '') return '—';
  return String(value);
}

function ComponentCard({ item }: { item: MonitoringComponent }) {
  const Icon = ICONS[item.key] || Network;
  const presentation = STATUS[item.status];
  const details = Object.entries(item.details || {});
  return <details className={`monitoring-component monitoring-tone-${item.status.toLowerCase()}`}>
    <summary>
      <span className="monitoring-component__icon"><Icon /></span>
      <span className="monitoring-component__copy"><b>{item.label}</b><small>{item.summary}</small></span>
      <span className="monitoring-component__status"><i />{presentation.label}</span>
      <ChevronDown className="monitoring-component__chevron" />
    </summary>
    <div className="monitoring-component__details">
      <div><span>Проверено</span><b>{formatDate(item.observedAt)}</b></div>
      <div><span>Время ответа</span><b>{item.latencyMs} мс</b></div>
      {details.map(([key, value]) => <div key={key}><span>{DETAIL_LABELS[key] || key}</span><b>{detailValue(value)}</b></div>)}
    </div>
  </details>;
}

function IncidentCard({
  incident,
  onAcknowledge,
  pending,
}: {
  incident: MonitoringIncident;
  onAcknowledge: (id: string) => void;
  pending: boolean;
}) {
  const active = incident.status === 'ACTIVE';
  const acknowledged = Boolean(incident.acknowledged_at);
  return <article className={`monitoring-incident ${active ? 'active' : 'resolved'} severity-${incident.severity.toLowerCase()}`}>
    <span className="monitoring-incident__marker">{active ? <AlertTriangle /> : <CheckCircle2 />}</span>
    <div className="monitoring-incident__body">
      <header><div><small>{incident.source} · {incident.code}</small><h3>{incident.title}</h3></div><span>{incident.severity === 'CRITICAL' ? 'Критично' : incident.severity === 'WARNING' ? 'Внимание' : 'Информация'}</span></header>
      <p>{incident.message}</p>
      <footer>
        <span>Последнее событие: {formatDate(incident.last_seen_at)}</span>
        <span>Повторений: {incident.occurrences}</span>
        {!active && <span>Восстановлено: {formatDate(incident.resolved_at)}</span>}
        {acknowledged && <span className="acknowledged"><CheckCircle2 /> Подтверждено {formatDate(incident.acknowledged_at)}</span>}
      </footer>
    </div>
    {active && incident.incident_type === 'SYSTEM' && !acknowledged && <button className="button ghost compact" type="button" disabled={pending} onClick={() => onAcknowledge(incident.id)}><CheckCircle2 /> {pending ? 'Подтверждаем…' : 'Подтвердить'}</button>}
  </article>;
}

export function MonitoringPage() {
  const { user } = useAuth();
  const admin = userHasRole(user, 'ADMIN');
  const queryClient = useQueryClient();
  const [telephonyExpanded, setTelephonyExpanded] = useState(false);
  const snapshotQuery = useQuery({
    queryKey: ['monitoring-snapshot'],
    queryFn: () => api<MonitoringSnapshot>('/monitoring'),
    enabled: admin,
    retry: false,
    refetchInterval: admin ? REFRESH_MS : false,
    refetchOnWindowFocus: true,
  });
  const acknowledge = useMutation({
    mutationFn: (id: string) => api(`/monitoring/incidents/${id}/acknowledge`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['monitoring-snapshot'] }),
  });

  if (!admin) return <div className="form-error">Мониторинг системы доступен только администратору.</div>;

  if (snapshotQuery.isLoading) return <main className="monitoring-page"><PageHeading eyebrow="СИСТЕМА" title="Мониторинг" description="Собираем состояние сервисов…" /><section className="surface monitoring-loading"><RefreshCw className="spinning" /><div><b>Проверяем инфраструктуру</b><small>Каждый сервис отвечает независимо</small></div></section></main>;

  if (!snapshotQuery.data) return <main className="monitoring-page"><PageHeading eyebrow="СИСТЕМА" title="Мониторинг" description="Состояние сервисов и инфраструктуры" /><section className="surface monitoring-load-error"><AlertTriangle /><div><h2>Не удалось получить снимок</h2><p>{snapshotQuery.error instanceof Error ? snapshotQuery.error.message : 'Сервер мониторинга временно недоступен'}</p><button className="button primary" type="button" onClick={() => { void snapshotQuery.refetch(); }}><RefreshCw /> Повторить</button></div></section></main>;

  const data = snapshotQuery.data;
  const presentation = STATUS[data.status];
  const HeroIcon = presentation.icon;
  const activeIncidents = data.incidents.filter(item => item.status === 'ACTIVE');
  const resolvedIncidents = data.incidents.filter(item => item.status === 'RESOLVED');

  return <main className="monitoring-page">
    <PageHeading
      eyebrow="СИСТЕМА"
      title="Мониторинг"
      description="Единое состояние сервисов, фоновых процессов и инфраструктуры"
      actions={<button className="button ghost" type="button" disabled={snapshotQuery.isFetching} onClick={() => { void snapshotQuery.refetch(); }}><RefreshCw className={snapshotQuery.isFetching ? 'spinning' : ''} /> {snapshotQuery.isFetching ? 'Обновляем…' : 'Обновить'}</button>}
    />

    <section className={`monitoring-hero monitoring-tone-${data.status.toLowerCase()}`} aria-live="polite">
      <span className="monitoring-hero__icon"><HeroIcon /></span>
      <div className="monitoring-hero__copy"><small>{presentation.label}</small><h2>{presentation.title}</h2><p>{presentation.note}</p></div>
      <div className="monitoring-hero__time"><Clock3 /><span><small>Снимок системы</small><b>{formatDate(data.observedAt)}</b></span></div>
      <div className="monitoring-hero__counts">
        <span><i className="healthy" /><b>{data.counts.healthy}</b><small>В норме</small></span>
        <span><i className="degraded" /><b>{data.counts.degraded}</b><small>Внимание</small></span>
        <span><i className="down" /><b>{data.counts.down}</b><small>Недоступно</small></span>
        <span><i className="unknown" /><b>{data.counts.unknown}</b><small>Нет данных</small></span>
      </div>
    </section>

    {(snapshotQuery.isError || acknowledge.isError) && <div className="admin-feedback error" role="alert">
      {acknowledge.error instanceof Error
        ? acknowledge.error.message
        : snapshotQuery.error instanceof Error
          ? snapshotQuery.error.message
          : 'Не удалось обновить состояние мониторинга'}
    </div>}

    <div className="monitoring-groups">
      {CATEGORIES.map(category => {
        const items = data.components.filter(item => item.category === category.id);
        if (!items.length) return null;
        return <section className="monitoring-group surface" key={category.id}>
          <header><div><h2>{category.title}</h2><p>{category.note}</p></div><span>{items.filter(item => item.status === 'HEALTHY').length} из {items.length} в норме</span></header>
          <div className="monitoring-component-grid">{items.map(item => <ComponentCard item={item} key={item.key} />)}</div>
          {category.id === 'TELEPHONY' && <button className="monitoring-diagnostics-toggle" type="button" onClick={() => setTelephonyExpanded(value => !value)} aria-expanded={telephonyExpanded}>{telephonyExpanded ? <ChevronUp /> : <Activity />}<span><b>{telephonyExpanded ? 'Скрыть диагностику телефонии' : 'Диагностика телефонии'}</b><small>FreeSWITCH, SIP, WebRTC, события, операторы и записи</small></span>{telephonyExpanded ? <ChevronUp /> : <ChevronDown />}</button>}
        </section>;
      })}
    </div>

    {telephonyExpanded && <section className="monitoring-telephony-details"><TelephonyDiagnosticsPanel /></section>}

    <section className="monitoring-incidents">
      <div className="monitoring-section-heading"><div><small>ЖУРНАЛ СОСТОЯНИЙ</small><h2>Инциденты</h2><p>Повторные наблюдения объединяются, а восстановленные сервисы закрываются автоматически.</p></div><span>{activeIncidents.length} активных</span></div>

      <div className="monitoring-incident-section surface">
        <header><div><AlertTriangle /><span><h3>Активные инциденты</h3><p>Требуют проверки или подтверждения администратора</p></span></div><b>{activeIncidents.length}</b></header>
        <div className="monitoring-incident-list">
          {activeIncidents.map(item => <IncidentCard incident={item} key={`${item.incident_type}-${item.id}`} onAcknowledge={id => acknowledge.mutate(id)} pending={acknowledge.isPending && acknowledge.variables === item.id} />)}
          {!activeIncidents.length && <div className="monitoring-empty"><CheckCircle2 /><span><b>Активных инцидентов нет</b><small>Все наблюдаемые отклонения устранены</small></span></div>}
        </div>
      </div>

      <div className="monitoring-incident-section surface resolved">
        <header><div><CheckCircle2 /><span><h3>Восстановлено за 24 часа</h3><p>Недавние проблемы, которые система закрыла автоматически</p></span></div><b>{resolvedIncidents.length}</b></header>
        <div className="monitoring-incident-list">
          {resolvedIncidents.map(item => <IncidentCard incident={item} key={`${item.incident_type}-${item.id}`} onAcknowledge={() => undefined} pending={false} />)}
          {!resolvedIncidents.length && <div className="monitoring-empty neutral"><Clock3 /><span><b>Нет восстановленных инцидентов</b><small>За последние сутки переходов в норму не зарегистрировано</small></span></div>}
        </div>
      </div>
    </section>
  </main>;
}
